import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { selectUsersFullPaginatedData } from 'src/app/Budgets/redux/budgets.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetRequestsBoxWithFilters from 'src/components/BudgetRequestsBoxWithFilters/BudgetRequestsBoxWithFilters'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { PusherEvents } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import usePusher from 'src/hooks/usePusher'
import { useTranslate } from 'src/localization/Trns'
import { BudgetRequestStatus } from 'src/models/budgetRequest.models'
import {
  budgetRequestsQueries,
  budgetRequestsQueryKeys,
  getBudgetRequestsLastPagination,
  getBudgetRequestsTotalItems,
  selectBudgetRequestListModels,
} from 'src/queries/budgetRequests/budgetRequests.queries'
import { getUsers } from 'src/redux/actions/data/users.actions'
import {
  selectBudgetsNeedsApprovalMatch,
  selectBudgetsRequestsMatch,
  selectMyWalletBudgetsPendingMatch,
} from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const BudgetsRequestContent: React.FC = () => {
  const translate = useTranslate()

  const budgetsRequestsMatch = useSelector(selectBudgetsRequestsMatch)
  const budgetsNeedsApprovalMatch = useSelector(selectBudgetsNeedsApprovalMatch)
  const myWalletBudgetsPendingMatch = useSelector(
    selectMyWalletBudgetsPendingMatch,
  )
  const role =
    budgetsRequestsMatch || budgetsNeedsApprovalMatch
      ? 'approver'
      : myWalletBudgetsPendingMatch
        ? 'requester'
        : 'approver'
  const appName = useSelector(selectAppName)
  const user = useCurrentUser()
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const { bind } = usePusher(user?.id)
  const queryClient = useQueryClient()
  const dispatch = useBackgroundDispatch()
  const budgetRequestsQuery = useInfiniteQuery(
    budgetRequestsQueries.list({
      [role]: 'me',
      count: 10,
      statuses: [BudgetRequestStatus.Pending],
    }),
  )
  const budgetRequests =
    budgetRequestsQuery.data !== undefined
      ? selectBudgetRequestListModels(budgetRequestsQuery.data)
      : []
  const budgetRequestCount = getBudgetRequestsTotalItems(
    budgetRequestsQuery.data,
  )
  const budgetRequestPagination = getBudgetRequestsLastPagination(
    budgetRequestsQuery.data,
  )

  bind(PusherEvents.BudgetRequest, (data: { action: string }) => {
    if (data.action === 'UPDATED' || data.action === 'CREATED') {
      queryClient.invalidateQueries({
        queryKey: budgetRequestsQueryKeys.all(),
      })
    }
  })

  const fetchBudgetRequests = (page?: number): void => {
    if (page === 0) {
      void budgetRequestsQuery.refetch()
      return
    }

    if (budgetRequestsQuery.hasNextPage) {
      void budgetRequestsQuery.fetchNextPage()
    }
  }

  const infiniteScrollProps = {
    loading:
      budgetRequestsQuery.isPending || budgetRequestsQuery.isFetchingNextPage,
    page: budgetRequestPagination?.page,
    complete: !budgetRequestsQuery.hasNextPage,
    fetch: fetchBudgetRequests,
  }

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers({
        q: filterInput,
        count: 10,
        page: page,
      }),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 122)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 122)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  return (
    <>
      <Helmet>
        <title>{`Budget Requests | ${appName}`}</title>
        <meta
          name="description"
          content={`Budget Requests page of ${appName}`}
        />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent noPadding>
          <BudgetRequestsBoxWithFilters
            budgetRequests={budgetRequests}
            isSlim={false}
            totalBudgetRequests={
              budgetRequestCount !== undefined ? budgetRequestCount : 0
            }
            defaultTitle={
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate(
                  role === 'approver'
                    ? budgetRequestCount === 1
                      ? '${count} Request'
                      : '${count} Requests'
                    : '${count} Pending',
                  {
                    count: `${budgetRequestCount}`,
                  },
                )}
              </span>
            }
            infiniteScrollProps={infiniteScrollProps}
            userInfiniteScrollProps={userInfiniteScrollProps}
            isApprover={role === 'approver'}
            clientHeight={clientHeight || 0}
          />
        </BackgroundPageContent>
      </div>
    </>
  )
}

export default BudgetsRequestContent
